export type CloudSubView = 'scope' | 'workbench' | 'console' | 'executive' | 'funnel' | 'portfolio' | 'categories' | 'validation';

export interface ScopeRow {
  scope: string;
  apps: number;
}

export interface BasisInfo {
  basis_key: string;
  title: string;
  description: string | null;
  predicate_text: string;
  removable?: boolean;
}

export interface ScopesResponse {
  basis: string;
  basis_title: string;
  basis_predicate: string;
  default_basis: string;
  extract: { batch_id: number; as_of: string | null } | null;
  bases: BasisInfo[];
  live_basis?: string;
  basis_fields?: string[];
  basis_ops?: string[];
  universe_total: number;
  basis_population: number;
  scopes: ScopeRow[];
}

export interface ScopedSummary {
  scope: string;
  basis: string;
  basis_predicate: string;
  extract: { batch_id: number; as_of: string | null } | null;
  in_scope: number;
  decided: number;
  undecided_records: number;
  no_decision: number;
  migrates: number;
  does_not_migrate: number;
  provenance: { rule: number; model: number; human: number };
}

export interface FunnelKpis {
  as_of: string | null;
  note: string;
  assessed: number;
  out_of_scope: number;
  scope_conflicts: number;
  scope_conflict_eais: number[];
  cloud_candidates: number;
  on_prem_candidates: number;
  in_progress: number;
  migrated: number;
  needs_decision: number;
  total_application_entities: number;
  by_disposition: Record<string, number>;
  by_category: Record<string, number>;
  by_progress: Record<string, number>;
  by_primary_cloud: Record<string, number>;
  phase3: {
    rows: number;
    hidden: number;
    eai_apps: number;
    by_category: Record<string, number>;
    by_track: Record<string, number>;
    by_status: Record<string, number>;
    by_entity_type: Record<string, number>;
  };
  universe: { as_of: string | null; batch_id: number; total: number; live: number } | null;
  impact: { available: boolean; message: string | null };
  target_50pct: { denominator: number; migrated: number; pct: number | null; note: string };
  baselines: {
    phase2: { id: number; as_of: string | null; filename: string | null; created_at: string } | null;
    phase3: { id: number; as_of: string | null; filename: string | null; created_at: string } | null;
  };
}

export interface PortfolioRow {
  id: number;
  eai_nbr: number | null;
  name: string;
  proposed_grouping: string | null;
  entity_type: string;
  sor_disposition: string | null;
  sor_tier: string | null;
  sor_rpo: string | null;
  sor_rto: string | null;
  rto_range: string | null;
  phc_disposition: string | null;
  new_tier: string | null;
  new_rpo: string | null;
  new_rto: string | null;
  pattern: string | null;
  primary_cloud_disposition: string | null;
  cloud_migration_progress: string | null;
  cloud_category: number | null;
  cloud_willingness: string | null;
  size_of_effort: string | null;
  cloud_target: string | null;
  phase3_status: string | null;
  phase3_owner: string | null;
  phase3_category: number | null;
  category_track: string | null;
  tracker_id: number | null;
  out_of_scope: boolean;
  oos_reason: string | null;
  scope_conflict: boolean;
  phase2_as_of: string | null;
  phase2_imported_at: string | null;
  phase2_batch_id: number | null;
}

export interface TrackerRow {
  id: number;
  entity_type: string;
  eai_nbr: number | null;
  parent_eai: number | null;
  name: string;
  app_count: number | null;
  category: number | null;
  category_track: string | null;
  subgroup: string | null;
  owner: string | null;
  current_status: string | null;
  assessed: string | null;
  landing_aligned: string | null;
  poa_decision: string | null;
  existing_timeline: string | null;
  existing_timeline_details: string | null;
  target_date: string | null;
  gfd_required: string | null;
  gfd_rag: string | null;
  funding: string | null;
  resources: string | null;
  blockers: string | null;
  complete: string | null;
  notes: string | null;
  hidden_in_source: boolean;
  extra: Record<string, unknown>;
}

export interface ImportBatch {
  id: number;
  source: string;
  as_of: string | null;
  filename: string | null;
  uploaded_by: number | null;
  row_counts: Record<string, number>;
  warnings: string[];
  created_at: string;
}

export interface AppDetail extends PortfolioRow {
  phase2_full: Record<string, unknown> | null;
  remediations: Array<{
    id: number;
    eai_nbr: number;
    app_name: string | null;
    correction: string | null;
    poc: string | null;
    status: string | null;
    notes: string | null;
  }>;
  tracker_items: TrackerRow[];
  baseball_cards: Array<{ id: number; suite_title: string | null; filename: string | null; related_eai: number[] }>;
  provenance: Array<{ field_key: string; value: string | null; source: string; ai_rule_version: string | null }>;
  impact: { available: boolean; rows: number };
}

export const CAT_LABEL: Record<number, string> = {
  0: 'Cat 0 · Platforms',
  1: 'Cat 1 · First wave',
  2: 'Cat 2 · Workflow / Reporting',
  3: 'Cat 3 · BA hold',
};
